import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';
import i18n from '@/i18n/config';
import apiClient, { setAuthToken, setRefreshToken, getAuthToken, clearAuth } from '@/utils/api';

const AuthContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Auth uses ONLY Authorization: Bearer tokens (no cookies, no withCredentials).
 *
 * Rationale: Cloudflare in front of the backend forces
 *   Access-Control-Allow-Origin: *
 * which is INCOMPATIBLE with `credentials: include` (browsers block it).
 * Bearer headers are not subject to that rule, so auth works on any
 * origin (preview, trabadju.cv, www.trabadju.cv, etc.).
 */

// Known raw backend detail strings mapped to translated messages
const KNOWN_BACKEND_ERRORS = {
  'Email already registered': () => i18n.t('auth.errorEmailExists'),
};

function formatApiErrorDetail(detail) {
  if (detail == null) return i18n.t('auth.errorGeneric');
  if (typeof detail === "string") return KNOWN_BACKEND_ERRORS[detail] ? KNOWN_BACKEND_ERRORS[detail]() : detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const authHeaders = () => {
  const t = getAuthToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [userType, setUserType] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (getAuthToken()) checkAuth();
    else setLoading(false);
  }, []);

  const checkAuth = async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setUserType(null);
      setLoading(false);
      return;
    }
    // Probe the endpoint matching the last known session type first, so a
    // candidate token never hits /auth/me (401) and vice-versa.
    const probes = localStorage.getItem('trabadju_session') === 'candidate'
      ? [['/candidates/me', 'candidate'], ['/auth/me', 'employer']]
      : [['/auth/me', 'employer'], ['/candidates/me', 'candidate']];
    let resolved = false;
    let networkError = false;
    try {
      for (const [url, type] of probes) {
        try {
          const { data } = await apiClient.get(url);
          setUser(data);
          setUserType(type);
          localStorage.setItem('trabadju_session', type);
          resolved = true;
          break;
        } catch (e) {
          if (!e?.response) { networkError = true; break; } // offline: keep tokens
        }
      }
      if (!resolved && !networkError) {
        clearAuth();
        setUser(null);
        setUserType(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAuthSuccess = (data, type) => {
    if (data?.verification_required) {
      return { success: false, verificationRequired: true, email: data.email, userType: data.user_type || type };
    }
    if (data?.access_token) setAuthToken(data.access_token);
    if (data?.refresh_token) setRefreshToken(data.refresh_token);
    // Strip tokens from user object before storing
    const { access_token, refresh_token, ...userInfo } = data;
    setUser(userInfo);
    setUserType(type);
    localStorage.setItem('trabadju_session', type);
    return { success: true, userType: type };
  };

  const handleAuthError = (error) => {
    // Distinguish: server returned a structured error vs network/CORS/server-down
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    if (status === 403 && detail?.code === 'EMAIL_NOT_VERIFIED') {
      return { success: false, verificationRequired: true, email: detail.email, userType: detail.user_type };
    }
    if (detail) {
      return { success: false, error: formatApiErrorDetail(detail) };
    }
    if (status) {
      // Server replied but without a usable detail (e.g., 500 HTML error page)
      return { success: false, error: i18n.t('auth.errorServer', { status }) };
    }
    // No response at all — likely network/CORS or backend offline
    return {
      success: false,
      error: i18n.t('auth.errorNetwork')
    };
  };

  const loginEmployer = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      // Backend returns user_type — respect it so candidates who use the main
      // login form still land on the correct dashboard.
      const type = data.user_type === 'candidate' ? 'candidate' : 'employer';
      return handleAuthSuccess(data, type);
    } catch (error) { return handleAuthError(error); }
  };

  const loginCandidate = async (email, password) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/candidates/login`, { email, password });
      return handleAuthSuccess(data, 'candidate');
    } catch (error) { return handleAuthError(error); }
  };

  const registerEmployer = async (userData) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/register`, { ...userData, referral_code: localStorage.getItem('trabadju_ref') || undefined });
      return handleAuthSuccess(data, 'employer');
    } catch (error) { return handleAuthError(error); }
  };

  const registerCandidate = async (userData) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/candidates/register`, { ...userData, referral_code: localStorage.getItem('trabadju_ref') || undefined });
      return handleAuthSuccess(data, 'candidate');
    } catch (error) { return handleAuthError(error); }
  };

  const verifyEmail = async (email, code) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/verify-email`, { email, code });
      return handleAuthSuccess(data, data.user_type === 'candidate' ? 'candidate' : 'employer');
    } catch (error) { return handleAuthError(error); }
  };

  const resendCode = async (email) => {
    try {
      await axios.post(`${API_URL}/api/auth/resend-code`, { email });
      return { success: true };
    } catch (error) { return handleAuthError(error); }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { headers: authHeaders() });
    } catch (e) { /* ignore */ }
    clearAuth();
    setUser(null);
    setUserType(null);
  };

  const value = {
    user, userType, loading, setUser,
    loginEmployer, loginCandidate, registerEmployer, registerCandidate,
    verifyEmail, resendCode,
    logout, checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
